// Part B pairwise precedence (docs/specs/2026-09-16-tycoon-composition.md, "Part B" and "Amendment 2026-09-23";
// docs/specs/2026-09-23-tycoon-partb-interfaces.md, "Sorter"). Every drawable fragment owns a conservative convex
// proxy; a candidate pair is ordered by crossing, bed, decal or separating-plane rules, and only when the proxies
// overlap on screen. precedenceGraph.ts builds the graph and the Kahn/Tarjan order.
import { rotate, type Rotation } from './projection.js';

export type XY = readonly [number, number];
export type Range = readonly [number, number];
export type Bounds = readonly [number, number, number, number];
export interface Proxy { id: string; footprint: readonly XY[]; z: Range }
export type FragmentRole = 'floor' | 'shadow' | 'bed-wall' | 'rails' | 'wall-face' | 'coping' | 'deck' | 'platform' | 'canopy' | 'car' | 'solid';
/**
 * "Rests on" membership: within one group a lower rank supports a higher one, so the support draws first wherever
 * their projections overlap (a required edge, like bed edges). Ranks: deck/trestle/berm, platform and below-rail wall
 * are bases (ramp support faces are the foundation under a deck); rails, floors, canopies and wall faces rest on them;
 * cars ride on their corridor's bases and surfaces.
 */
export interface Support { group: string; rank: number }
export const SUPPORT_RANK = { foundation: -1, base: 0, surface: 1, rider: 2 } as const;
/** Largest nominal height difference (units) at which a platform stands on a structure. */
export const PLATFORM_ON_STRUCTURE_H = 0.5;
/**
 * A corridor support group spans the whole corridor, but its structure rests locally: a static piece rests only on what
 * shares its stretch of corridor (overlapping ranges), never on the neighbouring module, which the separating planes
 * order. A ramp support face ordered before the neighbouring deck, or a deck before the neighbouring rails, contradicted
 * those planes and closed cycles through buildings beside the ramp (4 Av–9 St, B-001). Ranges that merely touch do not
 * overlap; a fragment without a range keeps the group-wide relation. Cars keep riding every piece of their corridor they
 * overlap (floor/rails → car, like the region-wide bed), and station and wall groups are unchanged.
 */
const isCorridorGroup = (support: Support): boolean => support.group.startsWith('support:');
const isRider = (support: Support): boolean => support.rank === SUPPORT_RANK.rider;
const sameStretch = (a: Range | null, b: Range | null): boolean => a === null || b === null || Math.min(a[1], b[1]) - Math.max(a[0], b[0]) > EPSILON;
/** Corridor surfaces (rails, floors) lie on the structure; a platform over one stands above it. */
const isSurface = (fragment: Fragment): boolean => fragment.support?.rank === SUPPORT_RANK.surface && fragment.support.group.startsWith('support:');
/** Corridor bases (deck, trestle, berm) and their foundations (ramp supports) are structures platforms stand on. */
const isStructure = (fragment: Fragment): boolean => fragment.support !== undefined && fragment.support !== null && fragment.support.rank <= SUPPORT_RANK.base && fragment.support.group.startsWith('support:');
export interface Fragment {
  id: string; proxy: Proxy; bounds: Bounds; corridorId: string | null; range: Range | null; role: FragmentRole;
  anchor: XY; height: number; dynamic: boolean; visible: boolean; crossingIds: readonly string[]; overlapRegionId: string | null;
  support?: Support | null;
  /** Bed rank override for components that join a bed without a bed role (ramp wall faces and copings). */
  bedRank?: number;
  /**
   * City scenery (buildings, trees): a tile-quantised prism on a layer that can be hidden. Cycle breaking drops an edge
   * touching scenery before any other geometric edge, so hiding the layer never leaves the network misordered.
   */
  scenery?: boolean;
  /** Dynamic only: ids of coincident instances sharing this exact proxy (includes `id`); emitted in stable-id order. */
  members?: readonly string[];
}
export type EdgeReason = 'geometry' | 'decal' | 'bed' | 'crossing' | 'support';
export interface PrecedenceEdge { before: string; after: string; reason: EdgeReason; crossingId: string | null; overlapArea: number }
export interface SortResult { order: string[]; edges: PrecedenceEdge[]; brokenEdges: PrecedenceEdge[] }
export interface CrossingStrata { id: string; lower: string; upper: string }

/** Toward-camera direction in (rotated x, rotated y, height units): the projection maps it to zero screen motion. */
export const CAMERA_DIRECTION = [1, 1, 4] as const;
export const GRID_CELL_PX = 64;
export const EPSILON = 1e-6;
const DECAL_LAYER: Partial<Record<FragmentRole, number>> = { floor: 0, shadow: 1, rails: 2 };
const BED_RANK: Partial<Record<FragmentRole, number>> = { floor: 0, 'bed-wall': 1, rails: 2 };
/** Bed rank of a wall in its bed: after the floor, before the rails. */
export const BED_WALL_RANK = 1;
export const REASON_CODE: readonly EdgeReason[] = ['geometry', 'decal', 'bed', 'crossing', 'support'];

export const isDecal = (role: FragmentRole): boolean => DECAL_LAYER[role] !== undefined;
export const unitScreen = (x: number, y: number, h: number): [number, number] => [32 * (x - y), 16 * (x + y) - 8 * h];

/** Unit-scale screen bounds of a proxy's projection at camera rotation `q` (absolute, camera-centre independent). */
export function proxyScreenBounds(proxy: Proxy, q: Rotation): [number, number, number, number] {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const point of proxy.footprint) {
    const [x, y] = rotate(point as [number, number], q);
    for (const h of proxy.z) {
      const [sx, sy] = unitScreen(x, y, h);
      if (sx < minX) minX = sx; if (sx > maxX) maxX = sx; if (sy < minY) minY = sy; if (sy > maxY) maxY = sy;
    }
  }
  return [minX, minY, maxX, maxY];
}

/** Rotation-compiled fragment (internal to the sorter modules). */
export interface Node {
  fragment: Fragment; xs: Float64Array; ys: Float64Array; z0: number; z1: number;
  depth: number; decalLayer: number; bedRank: number; hull?: [number, number][];
}
export const compile = (fragment: Fragment, q: Rotation): Node => {
  const n = fragment.proxy.footprint.length;
  if (n < 1) throw new Error(`proxy ${fragment.proxy.id} has an empty footprint`);
  const xs = new Float64Array(n), ys = new Float64Array(n);
  fragment.proxy.footprint.forEach((point, index) => { const [x, y] = rotate(point as [number, number], q); xs[index] = x; ys[index] = y; });
  const [ax, ay] = rotate(fragment.anchor as [number, number], q);
  const [z0, z1] = fragment.proxy.z;
  if (!(z0 <= z1)) throw new Error(`proxy ${fragment.proxy.id} has an inverted z range`);
  return { fragment, xs, ys, z0, z1, depth: ax + ay, decalLayer: DECAL_LAYER[fragment.role] ?? -1, bedRank: fragment.bedRank ?? BED_RANK[fragment.role] ?? -1 };
};

export const boundsOverlap = (a: Bounds, b: Bounds): boolean => a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];

/** Signed-distance extent of a footprint along unit normal (nx, ny). */
const extent = (node: Node, nx: number, ny: number, out: Float64Array): void => {
  let min = Infinity, max = -Infinity;
  for (let i = 0; i < node.xs.length; i += 1) { const d = node.xs[i]! * nx + node.ys[i]! * ny; if (d < min) min = d; if (d > max) max = d; }
  out[0] = min; out[1] = max;
};
const extentA = new Float64Array(2), extentB = new Float64Array(2);

/**
 * Separating-plane rule for one ordered pair. Returns 1 when `b` draws after `a`, -1 when `a` draws after `b`,
 * 0 when no candidate plane separates. Planes: edge planes of `a`, edge planes of `b`, then z planes.
 */
function separatingPlaneOrder(a: Node, b: Node): -1 | 0 | 1 {
  for (const owner of [a, b]) {
    const n = owner.xs.length;
    if (n < 2) continue;
    for (let i = 0; i < n; i += 1) {
      const j = (i + 1) % n, ex = owner.xs[j]! - owner.xs[i]!, ey = owner.ys[j]! - owner.ys[i]!, length = Math.hypot(ex, ey);
      if (length < EPSILON) continue;
      const nx = -ey / length, ny = ex / length, facing = nx * CAMERA_DIRECTION[0] + ny * CAMERA_DIRECTION[1];
      if (Math.abs(facing) < EPSILON) continue;
      // The plane passes through this edge: offset d. A separates when one fragment lies entirely on each side.
      const d = nx * owner.xs[i]! + ny * owner.ys[i]!;
      extent(a, nx, ny, extentA); extent(b, nx, ny, extentB);
      const aBelow = extentA[1]! <= d + EPSILON && extentB[0]! >= d - EPSILON, bBelow = extentB[1]! <= d + EPSILON && extentA[0]! >= d - EPSILON;
      if (aBelow === bBelow) continue; // not separating, or both degenerate on the plane
      // The camera side of the plane is the side along +normal when facing > 0.
      return (aBelow === facing > 0) ? 1 : -1;
    }
  }
  const aBelow = a.z1 <= b.z0 + EPSILON, bBelow = b.z1 <= a.z0 + EPSILON;
  if (aBelow !== bBelow) return aBelow ? 1 : -1; // (0,0,1)·(1,1,4) > 0: above is the camera side
  return 0;
}

export interface PairDecision { before: 0 | 1; reason: EdgeReason; crossingId: string | null }
/** Full pair rule: crossing, bed and support edges, then shadows first, then coplanar decals, then separating planes. */
export function decide(a: Node, b: Node, crossings: ReadonlyMap<string, CrossingStrata>): PairDecision | undefined {
  const fa = a.fragment, fb = b.fragment;
  // The zone is the set of lower-corridor fragments meeting the zone polygon; each precedes every overlapping
  // upper-corridor fragment, zone member or not.
  if ((fa.crossingIds.length || fb.crossingIds.length) && fa.corridorId !== null && fb.corridorId !== null && fa.corridorId !== fb.corridorId) {
    for (const id of fa.crossingIds) { const strata = crossings.get(id); if (strata && fa.corridorId === strata.lower && fb.corridorId === strata.upper) return { before: 0, reason: 'crossing', crossingId: id }; }
    for (const id of fb.crossingIds) { const strata = crossings.get(id); if (strata && fb.corridorId === strata.lower && fa.corridorId === strata.upper) return { before: 1, reason: 'crossing', crossingId: id }; }
  }
  if (fa.overlapRegionId !== null && fa.overlapRegionId === fb.overlapRegionId && a.bedRank >= 0 && b.bedRank >= 0 && a.bedRank !== b.bedRank) {
    return { before: a.bedRank < b.bedRank ? 0 : 1, reason: 'bed', crossingId: null };
  }
  const sa = fa.support, sb = fb.support;
  if (sa && sb && sa.group === sb.group && sa.rank !== sb.rank && (!isCorridorGroup(sa) || isRider(sa) || isRider(sb) || sameStretch(fa.range, fb.range))) {
    return { before: sa.rank < sb.rank ? 0 : 1, reason: 'support', crossingId: null };
  }
  // A platform stands on whatever structure (trestle, deck, berm) lies under it at its own level, whichever track's.
  if (fa.role === 'platform' && isStructure(fb) && Math.abs(fa.height - fb.height) <= PLATFORM_ON_STRUCTURE_H) return { before: 1, reason: 'support', crossingId: null };
  if (fb.role === 'platform' && isStructure(fa) && Math.abs(fa.height - fb.height) <= PLATFORM_ON_STRUCTURE_H) return { before: 0, reason: 'support', crossingId: null };
  // Translucent shadows are decals drawn first: before every other fragment they overlap, except coplanar decals,
  // which keep the declared layer order (floor < shadow < rails).
  const coplanarDecals = a.decalLayer >= 0 && b.decalLayer >= 0 && Math.abs(a.z0 - b.z0) < EPSILON;
  if (!coplanarDecals && (fa.role === 'shadow') !== (fb.role === 'shadow')) return { before: fa.role === 'shadow' ? 0 : 1, reason: 'decal', crossingId: null };
  if (coplanarDecals) {
    const before = a.decalLayer !== b.decalLayer ? (a.decalLayer < b.decalLayer ? 0 : 1) : (fa.id < fb.id ? 0 : 1);
    return { before, reason: 'decal', crossingId: null };
  }
  const order = separatingPlaneOrder(a, b);
  if (order === 0) {
    // No plane separates them: a platform stands over any corridor surface (rails, floor) it interpenetrates at its level.
    if (fa.role === 'platform' && isSurface(fb) && Math.abs(fa.height - fb.height) <= PLATFORM_ON_STRUCTURE_H) return { before: 1, reason: 'support', crossingId: null };
    if (fb.role === 'platform' && isSurface(fa) && Math.abs(fa.height - fb.height) <= PLATFORM_ON_STRUCTURE_H) return { before: 0, reason: 'support', crossingId: null };
    return undefined;
  }
  return { before: order === 1 ? 0 : 1, reason: 'geometry', crossingId: null };
}

/** Public single-pair query for tests and trace diagnostics. */
export function pairPrecedence(a: Fragment, b: Fragment, rotation: Rotation, crossings: readonly CrossingStrata[] = []): { before: string; after: string; reason: EdgeReason } | undefined {
  const na = compile(a, rotation), nb = compile(b, rotation), decision = decide(na, nb, new Map(crossings.map((item) => [item.id, item])));
  if (!decision || overlapArea(na, nb) <= MIN_OVERLAP_AREA) return undefined;
  const [before, after] = decision.before === 0 ? [a, b] : [b, a];
  return { before: before.id, after: after.id, reason: decision.reason };
}

// ---------------------------------------------------------------- projected overlap (cycle breaking only)

const hull = (points: [number, number][]): [number, number][] => {
  const sorted = [...points].sort((p, q) => p[0] - q[0] || p[1] - q[1]);
  if (sorted.length < 3) return sorted;
  const cross = (o: readonly number[], p: readonly number[], q: readonly number[]) => (p[0]! - o[0]!) * (q[1]! - o[1]!) - (p[1]! - o[1]!) * (q[0]! - o[0]!);
  const lower: [number, number][] = [], upper: [number, number][] = [];
  for (const p of sorted) { while (lower.length >= 2 && cross(lower.at(-2)!, lower.at(-1)!, p) <= 0) lower.pop(); lower.push(p); }
  for (const p of [...sorted].reverse()) { while (upper.length >= 2 && cross(upper.at(-2)!, upper.at(-1)!, p) <= 0) upper.pop(); upper.push(p); }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
};
const projectedHull = (node: Node): [number, number][] => {
  if (node.hull) return node.hull;
  const points: [number, number][] = [];
  for (let i = 0; i < node.xs.length; i += 1) for (const h of [node.z0, node.z1]) points.push(unitScreen(node.xs[i]!, node.ys[i]!, h));
  node.hull = hull(points);
  return node.hull;
};
const polygonArea = (polygon: readonly (readonly [number, number])[]): number => {
  let area = 0;
  for (let i = 0; i < polygon.length; i += 1) { const p = polygon[i]!, q = polygon[(i + 1) % polygon.length]!; area += p[0] * q[1] - q[0] * p[1]; }
  return Math.abs(area) / 2;
};
/** Sutherland–Hodgman clip of convex `subject` by convex counter-clockwise `clip`. */
const clipConvex = (subject: [number, number][], clip: [number, number][]): [number, number][] => {
  let output = subject;
  for (let i = 0; i < clip.length && output.length; i += 1) {
    const a = clip[i]!, b = clip[(i + 1) % clip.length]!, input = output;
    const inside = (p: readonly number[]) => (b[0] - a[0]) * (p[1]! - a[1]) - (b[1] - a[1]) * (p[0]! - a[0]) >= 0;
    const intersect = (p: [number, number], q: [number, number]): [number, number] => {
      const dx = q[0] - p[0], dy = q[1] - p[1], ex = b[0] - a[0], ey = b[1] - a[1], denominator = dx * ey - dy * ex;
      const t = denominator === 0 ? 0 : ((a[0] - p[0]) * ey - (a[1] - p[1]) * ex) / denominator;
      return [p[0] + dx * t, p[1] + dy * t];
    };
    output = [];
    for (let j = 0; j < input.length; j += 1) {
      const current = input[j]!, previous = input[(j + input.length - 1) % input.length]!;
      if (inside(current)) { if (!inside(previous)) output.push(intersect(previous, current)); output.push(current); }
      else if (inside(previous)) output.push(intersect(previous, current));
    }
  }
  return output;
};
/** Projected proxy overlap in unit-screen px². Degenerate (edge-on) hulls are thickened to a hairline. */
export const overlapArea = (a: Node, b: Node): number => {
  const pa = projectedHull(a), pb = projectedHull(b);
  if (pa.length < 3 || pb.length < 3) return 0;
  return polygonArea(clipConvex(pa, pb));
};
/**
 * Minimum projected overlap (unit px²) for an edge: one native pixel at the finest atlas level, (1/3)². Pairs whose
 * proxies only touch, or meet in a sub-pixel corner, stay unordered and take the Kahn priority.
 */
export const MIN_OVERLAP_AREA = 1 / 9;
